import { useId } from 'react';
import { apiHost } from '../../apiConfig';
import { Icon, Marking, ThemeToggle, cx, useMedia } from '../ui';
import AuthFigure, { FIGURE_BONES, FIGURE_JOINTS } from './AuthFigure';
import './auth.css';

/** Where each role lands after signing in. */
export const homeFor = (role) => (role === 'player' ? '/player-profile' : '/provider-profile');

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Parse a JSON error/success body without throwing on empty or non-JSON responses. */
export async function readJson(res) {
    try {
        return await res.json();
    } catch {
        return {};
    }
}

/**
 * AuthLayout — the signed-out frame from Auth.dc.html (public routes render outside AppShell).
 * One 440 px column, centred vertically; the decorative figure panel appears from 1024 px.
 */
export default function AuthLayout({ children }) {
    const wide = useMedia('(min-width: 1024px)');
    return (
        <div className="au-frame">
            <main className="au-main">
                <div className="au-brand">
                    <span className="au-brand__word">ENABLE</span>
                    <span className="au-brand__rule" aria-hidden="true" />
                    <span className="au-brand__sub">Games portal</span>
                    <span className="eg-spacer" />
                    <ThemeToggle compact={!wide} />
                </div>
                <div className="au-center">
                    <div className="au-col">{children}</div>
                </div>
                <div className="au-foot">
                    {/* title block: which portal this frame signs in to */}
                    <Marking name="enAble Games portal" lines={[[apiHost(), `© ${new Date().getFullYear()} enAble Games`]]} />
                </div>
            </main>
            {wide ? (
                <aside className="au-aside" aria-hidden="true">
                    <div className="au-aside__stick">
                        <AuthFigure />
                        <Marking className="au-aside__mark" name="Pose view" lines={[[`${FIGURE_JOINTS} joints`, `${FIGURE_BONES} bones`], ['X', 'Y', 'Z axes']]} />
                        <div className="au-aside__copy">
                            <span className="au-kicker">Rehabilitation exergames</span>
                            <span className="au-aside__title">Every movement measured, every session reviewed.</span>
                        </div>
                    </div>
                </aside>
            ) : null}
        </div>
    );
}

/** Caps kicker over the page title ("Welcome back / Log in"). */
export function AuthHead({ kicker, title }) {
    return (
        <div className="au-head">
            <span className="au-kicker">{kicker}</span>
            <h1 className="au-title">{title}</h1>
        </div>
    );
}

/** Form-level error: bold lead sentence, then detail, then an optional action. */
export function AuthAlert({ title, children, action }) {
    return (
        <div role="alert" className="au-alert">
            <strong>{title}</strong>
            {children ? <> {children}</> : null}
            {action ? <div>{action}</div> : null}
        </div>
    );
}

/**
 * AuthField — underline text field (EGDL). `trailing` sits at the right of the label row
 * (the password Show/Hide toggle); `children` render under the error (the password rules).
 */
export function AuthField({ label, error, trailing, children, describedBy, inputRef, id, ...input }) {
    const auto = useId();
    const fid = id || auto;
    const errId = fid + '-err';
    const described = [error ? errId : null, describedBy].filter(Boolean).join(' ') || undefined;
    return (
        <div className="eg-field au-field">
            <span className="au-field__top">
                <label htmlFor={fid} className="eg-field__label">{label}</label>
                {trailing}
            </span>
            <input ref={inputRef} id={fid} className="eg-input" aria-invalid={error ? true : undefined} aria-describedby={described} {...input} />
            {error ? (
                <span id={errId} className="eg-field__error"><Icon name="warning" size={16} />{error}</span>
            ) : null}
            {children}
        </div>
    );
}

const TILE = { accent: 'au-tile--accent', success: 'au-tile--success', danger: 'au-tile--danger' };

/**
 * AuthMessage — the message screens (check email, verifying, verified, link failed, 404):
 * 64 px icon tile, caps kicker, title and body, then the primary action / notes (children)
 * and a hairline row of caps links.
 */
export function AuthMessage({ tone = 'muted', icon, pulse, kicker, title, body, children, links }) {
    return (
        <>
            <span className={cx('au-tile', TILE[tone])} aria-hidden="true">
                {pulse ? <span className="au-pulse" /> : <Icon name={icon} size={32} strokeWidth={2} />}
            </span>
            <div role="status" className="au-head au-msg">
                <span className={cx('au-kicker', tone !== 'muted' && `au-kicker--${tone}`)}>{kicker}</span>
                <h1 className="au-title">{title}</h1>
                {body ? <span className="au-lede">{body}</span> : null}
            </div>
            {children}
            <div className="au-links">{links}</div>
        </>
    );
}
